import lodash from 'lodash';
import React, { Fragment, useEffect, useLayoutEffect } from 'react';
import { $Formutil, Form } from 'react-bootstrap-formutil';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Debug from 'components/Debug';
import Toast from 'components/Toast';
import store from 'stores/values';
import { EventParams } from 'types/common';
import { useTrack } from 'utils/track';

export interface ProgressFormProps<Fields = any, Validators = any, WeakFields = Fields> {
    $formutil: $Formutil<Fields, Validators, WeakFields>;
    onSubmit(next: string | (() => void), ev?: React.FormEvent): void;
    /**
     * @description 对比当前表单数据和缓存数据是否一致
     *  如果不一致会清除当前模块影响的后续模块的数据
     */
    onCleanEffect(shouldStorage: boolean): void;
}

// 开户页面的页面ID，用于缓存
export type OpenPageId =
    | 'category'
    | 'account'
    | 'ocr'
    | 'baseinfo' // 原基础信息页
    | 'baseinfo2' // 普通开户流程拆分出来的第二页基础信息
    | 'job'
    | 'invest'
    | 'beneficiary' // 受益人信息页面
    | 'contact'
    | 'revenue'
    | 'taxforms'
    | 'agreements'
    | 'deliveryAddress'
    | 'w8'
    | 'exemption'
    | 'assets'
    | 'address'
    | 'qualificationQuestionnaire' // 专业用户问卷页
    | 'residence';

interface ConfigType {
    id?: OpenPageId;
    step?: number; // 如果不展示进度条，可以不传
    title?: string;
    ensure?: OpenPageId | OpenPageId[];
    cache?: boolean;
    compare?: boolean; // 在清除effect模块之前, 是否要先比较当前模块前后数据是否发生变化
    effect?: OpenPageId | OpenPageId[]; // 受影响模块，当前模块修改后，则会将受影响的模块数据置空
    effectExcept?: OpenPageId | OpenPageId[]; // 除了effectExcept内的模块不受影响, 别的模块都受影响
    showProgressBar?: boolean;
    basePath: 'open' | 'esop' | 'link' | 'upgrade';
    eventParams?: {
        submitEventParams?: EventParams; // 提交的时候的神策埋点, 会自动加上提交时跟页面曝光的时间差
        exposureEventParams?: EventParams; // 页面曝光时候的神策埋点
    };
}

function judgeMissingEnsure(ensureData: Record<string, any> | undefined) {
    // 如果是通过effect删除了某一项数据，那么该模块数据为空对象，也需要判断
    return !ensureData || Object.keys(ensureData).length === 0;
}

/**
 * @desc 开户步骤组件所需要的高阶组件
 * @param {Number} step 开户进度
 *
 *
 * @usage withStepForm(1)(YourComponent)
 */
function createWithStepForm(config: ConfigType): HOC<ProgressFormProps>;
function createWithStepForm(step: number, title: string): HOC<ProgressFormProps>;
function createWithStepForm(...args): HOC<ProgressFormProps> {
    let step,
        title,
        id,
        ensure,
        cache = true,
        basePath = 'open',
        showProgressBar,
        compare = true,
        effect,
        effectExcept,
        eventParams: ConfigType['eventParams'];

    if (typeof args[0] === 'object') {
        ({
            step,
            title,
            id,
            ensure,
            cache = true,
            basePath,
            showProgressBar,
            eventParams,
            compare = true,
            effect,
            effectExcept
        } = args[0]);
    } else {
        [step, title] = args;
    }

    if (!id) {
        id = step;
    }

    return function withStepForm(WrappedComponent: any): any {
        const AccountStepForm: React.FC = AccountStepFormProps => {
            const location = useLocation();
            const navigate = useNavigate();
            const { trackEvent } = useTrack();

            useLayoutEffect(() => {
                step && store.setStep(step);
                store.setTitle(title);
                window.scrollTo(0, 0);

                if (typeof showProgressBar !== 'undefined') {
                    store.setShowProgressBar(showProgressBar);
                }
            }, []);

            useEffect(() => {
                const { exposureEventParams } = eventParams || {};

                if (exposureEventParams) {
                    trackEvent(exposureEventParams);
                }
            }, [trackEvent]);

            if (ensure) {
                const missing = Array.isArray(ensure)
                    ? ensure.some(item => judgeMissingEnsure(store.getParams(item)))
                    : judgeMissingEnsure(store.getParams(ensure));

                // 到该步骤所需的数据是否有遗漏，如有遗漏，跳转到对应模块功能首页
                if (missing) {
                    return (
                        <Navigate
                            replace
                            state={location.state}
                            to={{
                                ...location,
                                pathname: `/${basePath}`
                            }}
                        />
                    );
                }
            }

            return (
                <Form
                    $defaultValues={store.getParams(id)}
                    render={$formutil => {
                        const onCleanEffect: ProgressFormProps['onCleanEffect'] = shouldStorage => {
                            const { $params } = $formutil.$new();

                            // 如果需要做缓存处理，就将该页面表单值存进values
                            // 这里用JSON处理，是深拷贝的取巧用法。
                            // 深拷贝是为了避免组件移除时导致的表单项变化引起数据的变化
                            const currentParams = JSON.parse(JSON.stringify($params));
                            const oldParams = store.getParams(id);

                            if (compare && !lodash.isEqual(currentParams, oldParams)) {
                                // effectExcept优先级更高
                                if (effectExcept) {
                                    if (Array.isArray(effectExcept)) {
                                        Object.keys(store.paramsMap).forEach(itemKeyId => {
                                            // 如果当前key不在effectExcept中，就清除该key的数据
                                            if (!effectExcept.includes(itemKeyId)) {
                                                store.mergeParams(itemKeyId, {});
                                            }
                                        });
                                    } else {
                                        Object.keys(store.paramsMap).forEach(itemKeyId => {
                                            if (effectExcept !== itemKeyId) {
                                                store.mergeParams(itemKeyId, {});
                                            }
                                        });
                                    }
                                } else if (effect) {
                                    // 将需要清除的模块的数据清空
                                    if (Array.isArray(effect)) {
                                        effect.forEach(itemId => store.mergeParams(itemId, {}));
                                    } else {
                                        store.mergeParams(effect, {});
                                    }
                                }

                                shouldStorage && store.mergeParams(id, currentParams);
                            }
                        };

                        const onSubmit = async (next: string | (() => void), ev?: React.FormEvent) => {
                            if (ev) {
                                ev.preventDefault();
                            }

                            const { $invalid, $batchState, $getFirstError } = $formutil;

                            if ($invalid) {
                                $batchState({
                                    $dirty: true,
                                    $touched: true
                                });

                                Toast.show($getFirstError());
                            } else {
                                if (cache) {
                                    // 缓存数据同时处理对其它模块的副作用
                                    onCleanEffect(true);
                                }

                                // 页面点击提交按钮时候的埋点
                                if (eventParams?.submitEventParams) {
                                    trackEvent(eventParams.submitEventParams);
                                }

                                if (typeof next === 'function') {
                                    await next();
                                } else {
                                    navigate(next);
                                }
                            }
                        };

                        return (
                            <Fragment>
                                <WrappedComponent
                                    {...AccountStepFormProps}
                                    $formutil={$formutil}
                                    onSubmit={onSubmit}
                                    onCleanEffect={onCleanEffect}
                                />
                                <Debug data={$formutil.$params} title="$params" />
                                <Debug data={$formutil.$errors} title="$errors" />
                            </Fragment>
                        );
                    }}
                />
            );
        };

        AccountStepForm.displayName = `WithStepForm.${WrappedComponent.displayName}`;

        return AccountStepForm;
    };
}

export default createWithStepForm;
